// Loads the SKU -> Category mapping from the static JSON in /public and builds
// efficient lookup structures. The JSON is fetched once and cached, so adding new
// SKUs/categories to Openprint_SKU_Reportv1.json automatically shows up in the filter
// options with no code changes.

const MAPPING_URL = "/Openprint_SKU_Reportv1.json";

let cachedPromise = null;

function buildCategoryIndex(rows) {
  // skuToCategory: fast SKU -> Category lookup (source of truth is the SKU).
  // categoryToSkus: Category -> [SKU, ...], used to send a category's SKUs to the backend.
  const skuToCategory = new Map();
  const categoryToSkus = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row || row.SKU === null || row.SKU === undefined) return;
    const sku = String(row.SKU).trim();
    const category = row.Category ? String(row.Category).trim() : "";
    if (!sku || !category) return;

    skuToCategory.set(sku, category);
    if (!categoryToSkus.has(category)) {
      categoryToSkus.set(category, []);
    }
    categoryToSkus.get(category).push(sku);
  });

  const categories = Array.from(categoryToSkus.keys()).sort((a, b) =>
    a.localeCompare(b),
  );

  return { skuToCategory, categoryToSkus, categories };
}

// Returns a cached promise resolving to { skuToCategory, categoryToSkus, categories }.
export function loadProductCategories() {
  if (cachedPromise) {
    return cachedPromise;
  }
  cachedPromise = fetch(MAPPING_URL)
    .then((res) => {
      if (!res.ok) {
        throw new Error(`Failed to load product mapping (${res.status})`);
      }
      return res.json();
    })
    .then(buildCategoryIndex)
    .catch((err) => {
      // Allow a later retry if the first load failed.
      cachedPromise = null;
      throw err;
    });
  return cachedPromise;
}

// Buckets backend per-SKU item totals into per-category totals using the SKU->Category map.
// Returns { totals: { category: qty }, all: totalAcrossEverySku }. SKUs absent from the
// mapping still count toward `all` (the "All Products" total) but not toward any category.
export function buildCategoryTotals(skuTotals, skuToCategory) {
  const totals = {};
  let all = 0;
  Object.entries(skuTotals || {}).forEach(([sku, qty]) => {
    const n = Number(qty) || 0;
    all += n;
    const category = skuToCategory ? skuToCategory.get(String(sku)) : undefined;
    if (category) {
      totals[category] = (totals[category] || 0) + n;
    }
  });
  return { totals, all };
}
